// Home1.jsx
import React from "react";
import { Link } from "react-router-dom";
import { FaUserShield, FaUserTie } from "react-icons/fa";
import { MdDashboard, MdArrowForward, MdCalculate } from "react-icons/md";
import { useSelector } from "react-redux";
import { selectIsLoggedIn } from "../../redux/features/auth/authSlice";
import logo from "../../assets/megertuhome.png";


const ROLE_CARDS = [
  {
    icon: <FaUserShield size={36} className="text-primary" />,
    title: "Admin",
    bg: "bg-primary bg-opacity-10",
    border: "border-primary border-opacity-25",
    notes: [
      "Full access to all modules",
      "Manage users and secret keys",
      "View reports and tax estimates",
      "Manage products, batches and sales",
    ],
  },
  {
    icon: <FaUserTie size={36} className="text-success" />,
    title: "Employee",
    bg: "bg-success bg-opacity-10",
    border: "border-success border-opacity-25",
    notes: [
      "Create and view sales",
      "View products and stock levels",
      "Manage orders and customers",
      "Cannot access reports or user settings",
    ],
  },
];

export const Home1 = () => {
  const isLoggedIn = useSelector(selectIsLoggedIn);

  return (
    <div className="min-vh-100 d-flex flex-column" style={{ background: "linear-gradient(135deg, #f0f4ff 0%, #f8f9fa 60%, #e8f5e9 100%)" }}>

      {/* ── Navbar ───────────────────────────────────────────── */}
      <nav className="navbar bg-white border-bottom shadow-sm px-4 py-3">
        <div className="container-fluid px-0">
          <div className="d-flex align-items-center gap-3">
            <img src={logo} alt="logo"
              style={{ height: 44, width: "auto", objectFit: "contain" }} />
            <div>
              <p className="fw-bold fs-3 mb-0 lh-1"><span className="text-warning">Megertu</span> Ceramics</p>
            </div>
          </div>
          <div className="d-none d-lg-block" />
          <div className="col-md text-end justify-content-center">
            <a href="https://ceramics-calculator.vercel.app/"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary me-3 mt-1 mb-1 fs-3">
              <MdCalculate size={18} className="mb-1"/>
              Calculator
            </a>

          {isLoggedIn ? (
            <Link to="/dashboard"
              className="btn btn-primary me-3 mt-1 mb-1 fs-3">
              <MdDashboard size={20} /> Dashboard
            </Link>
          ) : (
            <Link to="/login"
              className="btn btn-primary me-3 mt-1 mb-1 fs-3">
              Sign In <MdArrowForward size={18} />
            </Link>
          )}
          </div>
        </div>
      </nav>

      {/* ── Main ─────────────────────────────────────────────── */}
      <main className="flex-grow-1 d-flex align-items-center py-5">
        <div className="container py-3">
          <div className="row g-5 align-items-center">

            {/* Left: big logo + title */}
            <div className="col-12 col-lg-7 d-flex flex-column align-items-center text-center">
              <img
                src={logo}
                alt="Yebirihan Technology Logo"
                style={{ width: "min(400px, 90%)", marginBottom: "30px" }}
              />
              <h1 className="fw-bold mb-2" style={{ fontSize: "36px" }}>
                Inventory Management System
              </h1>
              <p className="fs-4 text-secondary mb-1">
                Manage your ceramic store efficiently with real-time inventory tracking and reporting.
              </p>
              <p className="fs-4 mb-4" style={{ color: "#777" }}>
                Developed by <strong>Yebirihan Technology</strong>
              </p>

              {isLoggedIn ? (
                <Link to="/dashboard"
                  className="btn btn-primary btn-lg rounded-3 fw-semibold fs-4 d-inline-flex align-items-center gap-2">
                  <MdDashboard size={22} /> Go to Dashboard
                </Link>
              ) : (
                <Link to="/login"
                  className="btn btn-primary btn-lg rounded-3 fw-semibold fs-4 d-inline-flex align-items-center gap-2">
                  Sign In <MdArrowForward size={20} />
                </Link>
              )}
            </div>

            {/* Right: role cards */}
            <div className="col-12 col-lg-5">
              <p className="text-muted fw-semibold fs-4 text-uppercase mb-3"
                style={{ letterSpacing: "0.1em" }}>
                User Roles
              </p>

              {/* flex-wrap with min-width ensures cards stay wide enough before wrapping */}
              <div className="d-flex flex-wrap gap-3">
                {ROLE_CARDS.map(({ icon, title, bg, border, notes }) => (
                  <div key={title} className={`card border rounded-4 flex-grow-1 ${border} shadow-sm`}
                    style={{ minWidth: "240px" }}>
                    <div className="card-body p-4">

                      {/* Role header */}
                      <div className={`d-inline-flex align-items-center justify-content-center rounded-3 p-3 mb-3 ${bg}`}>
                        {icon}
                      </div>
                      <h4 className="fw-bold fs-3 mb-3">{title}</h4>

                      {/* Notes */}
                      <div className="d-flex flex-column gap-2">
                        {notes.map((note) => (
                          <div key={note} className="d-flex align-items-start gap-2">
                            <span className="text-success fw-bold fs-4 flex-shrink-0"
                              style={{ marginTop: "1px" }}>✓</span>
                            <span className="text-muted fs-4">{note}</span>
                          </div>
                        ))}
                      </div>

                      {/* Login link */}
                      {!isLoggedIn && (
                        <Link style={{marginLeft: "3px"}} to="/login"
                          className={`btn mt-4 rounded-3 fw-semibold fs-4 w-100
                            d-flex align-items-center justify-content-center gap-2
                            ${title === "Admin" ? "btn-outline-primary" : "btn-outline-success"}`}>
                          Login as {title}
                          <MdArrowForward size={16} />
                        </Link>
                      )}

                    </div>
                  </div>
                ))}
              </div>

              {/* Info note */}
              <div className="alert alert-info border-0 rounded-3 mt-3 fs-4 d-flex align-items-start gap-3 mb-0">
                <span className="fs-3 flex-shrink-0">ℹ️</span>
                <div>
                  <strong>First time logging in?</strong>{" "}
                  After signing in, go to <strong>Profile</strong> and update your password.
                  If you don't have an account, contact your administrator.
                </div>
              </div>

            </div>
          </div>
        </div>
      </main>

      {/* ── Footer ───────────────────────────────────────────── */}
      <footer className="bg-white border-top py-3 text-center">
        <p className="text-muted fs-5 mb-0">
          &copy; {new Date().getFullYear()} Megertu Ceramics &mdash;
          Developed by <strong>Yebirihan Technology</strong>
        </p>
      </footer>

    </div>
  );
};